import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { LEAVE } from "../constants/testIds";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Plus, ThumbsUp, ThumbsDown } from "@phosphor-icons/react";

const TYPES = [
  { v: "annual", l: "Annual" },
  { v: "sick", l: "Sick" },
  { v: "casual", l: "Casual" },
];

function StatusBadge({ s }) {
  const map = {
    pending: "bg-amber-50 text-amber-800 border-amber-200",
    approved: "bg-emerald-50 text-emerald-800 border-emerald-200",
    rejected: "bg-red-50 text-red-800 border-red-200",
  };
  return <Badge variant="outline" className={`rounded-sm ${map[s] || ""}`}>{s}</Badge>;
}

export default function Leaves() {
  const { user, tenantId } = useAuth();
  const [mine, setMine] = useState([]);
  const [balance, setBalance] = useState([]);
  const [all, setAll] = useState([]);
  const [empMap, setEmpMap] = useState({});
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ leave_type: "annual", start_date: "", end_date: "", day_part: "full", reason: "" });
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  const canApprove = ["super_admin", "company_admin", "hr", "manager"].includes(user?.role);

  const load = () => {
    if (user?.employee_id) {
      api.get("/leaves/mine").then(({ data }) => setMine(data));
      api.get("/leaves/balance").then(({ data }) => setBalance(data)).catch(() => setBalance([]));
    }
    if (canApprove) {
      const params = user?.role === "super_admin" && tenantId ? { tenant_id: tenantId } : {};
      api.get("/leaves", { params }).then(({ data }) => setAll(data));
      api.get("/employees", { params }).then(({ data }) => {
        const m = {}; for (const e of data) m[e.id] = e; setEmpMap(m);
      });
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tenantId, user?.role]);

  const submit = async () => {
    setSaving(true); setErr("");
    try {
      await api.post("/leaves", form);
      toast.success("Leave request submitted");
      setOpen(false);
      setForm({ leave_type: "annual", start_date: "", end_date: "", day_part: "full", reason: "" });
      load();
    } catch (e) { setErr(formatApiError(e)); } finally { setSaving(false); }
  };

  const decide = async (id, status) => {
    try { await api.patch(`/leaves/${id}`, { status }); toast.success(`Leave ${status}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const cancel = async (id) => {
    if (!window.confirm("Cancel this leave request?")) return;
    try { await api.delete(`/leaves/${id}`); toast.success("Leave request cancelled"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const availFor = (t) => balance.find((b) => b.leave_type === t)?.available;

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-emerald-700">Time off</div>
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight">Leaves</h1>
          <p className="text-sm text-zinc-500 mt-2">Apply for time off and track approvals.</p>
        </div>
        {user?.employee_id && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid={LEAVE.applyBtn} className="btn-zenith rounded-sm"><Plus size={16} className="mr-1.5" /> Apply for leave</Button>
            </DialogTrigger>
            <DialogContent data-testid={LEAVE.applyDialog}>
              <DialogHeader><DialogTitle className="font-display text-2xl">Apply for leave</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2 space-y-1">
                  <Label className="text-xs">Type</Label>
                  <Select value={form.leave_type} onValueChange={(v) => setForm({ ...form, leave_type: v })}>
                    <SelectTrigger className="rounded-sm" data-testid={LEAVE.typeSelect}><SelectValue /></SelectTrigger>
                    <SelectContent>{TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
                  </Select>
                  {availFor(form.leave_type) != null && (
                    <div className="text-xs text-zinc-500 mt-1" data-testid="leave-available-hint">
                      <span className="font-semibold text-emerald-700">{availFor(form.leave_type)}</span> day(s) available
                    </div>
                  )}
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">From</Label>
                  <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value, end_date: form.day_part === "full" ? form.end_date : e.target.value })} className="rounded-sm" data-testid={LEAVE.startInput} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">To</Label>
                  <Input type="date" value={form.day_part === "full" ? form.end_date : form.start_date} disabled={form.day_part !== "full"} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="rounded-sm" data-testid={LEAVE.endInput} />
                </div>
                <div className="col-span-2 space-y-1">
                  <Label className="text-xs">Duration</Label>
                  <Select value={form.day_part} onValueChange={(v) => setForm({ ...form, day_part: v, end_date: v === "full" ? form.end_date : form.start_date })}>
                    <SelectTrigger className="rounded-sm" data-testid="leave-day-part"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="full">Full day</SelectItem>
                      <SelectItem value="first_half">First half (half day)</SelectItem>
                      <SelectItem value="second_half">Second half (half day)</SelectItem>
                    </SelectContent>
                  </Select>
                  {form.day_part !== "full" && <div className="text-[11px] text-zinc-500">Half-day leave applies to the "From" date only and counts as 0.5 day.</div>}
                </div>
                <div className="col-span-2 space-y-1">
                  <Label className="text-xs">Reason</Label>
                  <Textarea value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className="rounded-sm" placeholder="Optional note for your manager" data-testid={LEAVE.reasonInput} />
                </div>
              </div>
              {err && <div className="text-sm text-red-600 border border-red-200 bg-red-50 px-3 py-2 rounded-sm">{err}</div>}
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
                <Button onClick={submit} disabled={saving} className="btn-zenith rounded-sm" data-testid={LEAVE.submitBtn}>{saving ? "Submitting…" : "Submit"}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {user?.employee_id && (
        <section>
          <h2 className="text-xs uppercase tracking-widest text-zinc-500 mb-2">My balance ({new Date().getFullYear()})</h2>
          <div className="grid grid-cols-3 gap-3" data-testid="leave-balance-strip">
            {balance.map((b) => (
              <div key={b.leave_type} className="border border-zinc-200 bg-white rounded-lg p-4" data-testid={`leave-balance-${b.leave_type}`}>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 capitalize">{b.leave_type}</div>
                <div className="font-display text-3xl font-bold mt-1">{b.available}</div>
                <div className="text-xs text-zinc-500 mt-0.5">of {b.entitled} · {b.used} used</div>
                {b.carried_forward > 0 && <div className="text-[10px] text-emerald-700 mt-0.5" data-testid={`leave-carried-${b.leave_type}`}>incl. {b.carried_forward} carried over from {new Date().getFullYear() - 1}</div>}
              </div>
            ))}
            {balance.length === 0 && <div className="col-span-3 text-sm text-zinc-500">No leave policy configured for your company yet.</div>}
          </div>
        </section>
      )}

      {user?.employee_id && (
        <section>
          <h2 className="text-xs uppercase tracking-widest text-zinc-500 mb-2">My requests</h2>
          <div className="overflow-x-auto border border-zinc-200 rounded-sm">
            <table className="hr-table" data-testid={LEAVE.myList}>
              <thead><tr><th>Type</th><th>From</th><th>To</th><th>Days</th><th>Status</th><th>Reason</th><th></th></tr></thead>
              <tbody>
                {mine.length === 0 && <tr><td colSpan={7} className="text-center py-6 text-zinc-500 text-sm">You haven't applied for any leaves yet.</td></tr>}
                {mine.map((l) => (
                  <tr key={l.id}>
                    <td className="capitalize">{l.leave_type}</td>
                    <td className="font-mono-plex text-xs">{l.start_date}</td>
                    <td className="font-mono-plex text-xs">{l.end_date}</td>
                    <td className="font-mono-plex text-xs">{l.days}</td>
                    <td><StatusBadge s={l.status} /></td>
                    <td className="text-xs text-zinc-600">{l.reason || "—"}</td>
                    <td>
                      {l.status === "pending" ? (
                        <button className="text-xs px-2 py-1 border border-red-300 text-red-700 hover:bg-red-50 rounded-sm transition-colors" onClick={() => cancel(l.id)} data-testid={`leave-cancel-${l.id}`}>Cancel</button>
                      ) : <span className="text-xs text-zinc-400">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {canApprove && (
        <section>
          <h2 className="text-xs uppercase tracking-widest text-zinc-500 mb-2">All requests</h2>
          <div className="overflow-x-auto border border-zinc-200 rounded-sm">
            <table className="hr-table" data-testid={LEAVE.adminList}>
              <thead><tr><th>Employee</th><th>Type</th><th>From</th><th>To</th><th>Days</th><th>Status</th><th>Actions</th></tr></thead>
              <tbody>
                {all.length === 0 && <tr><td colSpan={7} className="text-center py-6 text-zinc-500 text-sm">No leave requests to review.</td></tr>}
                {all.map((l) => {
                  const emp = empMap[l.employee_id];
                  return (
                    <tr key={l.id}>
                      <td>
                        <div className="text-sm">{emp ? `${emp.first_name} ${emp.last_name}` : l.employee_id}</div>
                        <div className="text-xs text-zinc-500">{emp?.employee_code}</div>
                      </td>
                      <td className="capitalize">{l.leave_type}</td>
                      <td className="font-mono-plex text-xs">{l.start_date}</td>
                      <td className="font-mono-plex text-xs">{l.end_date}</td>
                      <td className="font-mono-plex text-xs">{l.days}</td>
                      <td><StatusBadge s={l.status} /></td>
                      <td>
                        {l.status === "pending" ? (
                          <div className="flex gap-1">
                            <button className="text-xs px-2 py-1 border border-emerald-300 text-emerald-800 hover:bg-emerald-50 rounded-sm transition-colors duration-150" onClick={() => decide(l.id, "approved")} data-testid={LEAVE.approveBtn(l.id)}>
                              <ThumbsUp size={12} className="inline mr-1" /> Approve
                            </button>
                            <button className="text-xs px-2 py-1 border border-red-300 text-red-700 hover:bg-red-50 rounded-sm transition-colors duration-150" onClick={() => decide(l.id, "rejected")} data-testid={LEAVE.rejectBtn(l.id)}>
                              <ThumbsDown size={12} className="inline mr-1" /> Reject
                            </button>
                          </div>
                        ) : (
                          <span className="text-xs text-zinc-400">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}
